import { useEffect, useMemo, useRef, useState } from 'react';

const FLOOR_PATTERN = /(?:piso|floor)[\s_-]*0*(\d{1,2})(?:\D|$)/i;
const EXACT_UNIT_PATTERN = /^_?0*(\d{3,4})$/;
const NAMED_UNIT_PATTERN = /(?:departamento|depto|dpto|unidad|unit)[\s_-]*0*(\d{3,4})(?:\D|$)/i;
const TRAILING_UNIT_PATTERN = /(?:^|[\s_-])0*(\d{3,4})$/;

function decodeIllustratorName(value = '') {
  return String(value).replace(/_x([0-9a-f]{2,4})_/gi, (_, hex) => (
    String.fromCharCode(Number.parseInt(hex, 16))
  ));
}

function getLayerNames(node) {
  return [
    node.getAttribute('data-name'),
    node.getAttribute('aria-label'),
    node.getAttribute('inkscape:label'),
    node.getAttribute('label'),
    node.id,
  ]
    .filter(Boolean)
    .map(decodeIllustratorName);
}

function getFloorNumber(node) {
  for (const name of getLayerNames(node)) {
    const match = name.match(FLOOR_PATTERN);
    if (match) return String(Number(match[1]));
  }
  return '';
}

function getUnitNumber(node) {
  for (const name of getLayerNames(node)) {
    const exactMatch = name.trim().match(EXACT_UNIT_PATTERN);
    if (exactMatch) return String(Number(exactMatch[1]));

    const namedMatch = name.match(NAMED_UNIT_PATTERN);
    if (namedMatch) return String(Number(namedMatch[1]));

    const trailingMatch = name.match(TRAILING_UNIT_PATTERN);
    if (trailingMatch && !/^(?:cls|layer|capa)[\s_-]/i.test(name)) {
      return String(Number(trailingMatch[1]));
    }
  }
  return '';
}

function removeUnsafeSvgContent(svg) {
  svg.querySelectorAll('script, foreignObject').forEach((node) => node.remove());

  svg.querySelectorAll('*').forEach((node) => {
    for (const attribute of [...node.attributes]) {
      if (attribute.name.toLowerCase().startsWith('on')) {
        node.removeAttribute(attribute.name);
      }
    }
  });
}

function normalizeSvgMarkup(rawMarkup, mode) {
  const parser = new DOMParser();
  const documentNode = parser.parseFromString(rawMarkup, 'image/svg+xml');
  const svg = documentNode.documentElement;

  if (svg.nodeName.toLowerCase() !== 'svg' || documentNode.querySelector('parsererror')) {
    throw new Error('El archivo no contiene un SVG válido.');
  }

  removeUnsafeSvgContent(svg);
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('data-availability-svg', mode);

  svg.querySelectorAll('*').forEach((node) => {
    const isBuildingReference = getLayerNames(node).some((name) => (
      /^(?:edificio|building)$/i.test(name.trim())
    ));
    if (isBuildingReference) node.setAttribute('data-reference-layer', 'true');
  });

  let recognizedLayers = 0;

  if (mode === 'building') {
    svg.querySelectorAll('*').forEach((node) => {
      const floor = getFloorNumber(node);
      if (!floor) return;

      node.setAttribute('data-floor', floor);
      node.setAttribute('role', 'button');
      node.setAttribute('tabindex', '0');
      node.setAttribute('aria-label', `Piso ${floor}`);
      recognizedLayers += 1;
    });
  } else {
    svg.querySelectorAll('*').forEach((node) => {
      const unit = getUnitNumber(node);
      if (!unit) return;
      node.setAttribute('data-unit', unit);
      node.setAttribute('role', 'button');
      node.setAttribute('tabindex', '0');
      node.setAttribute('aria-label', `Departamento ${unit}`);
      recognizedLayers += 1;
    });
  }

  if (!recognizedLayers) {
    throw new Error(
      mode === 'building'
        ? 'El SVG no contiene capas identificables como Piso1…Piso7.'
        : 'El SVG no contiene departamentos identificables por número.'
    );
  }

  return new XMLSerializer().serializeToString(svg);
}

const InlineAvailabilitySvg = ({
  src,
  mode,
  availableValues = [],
  selectedValue = null,
  colorMap = {},
  onSelect,
  className = '',
}) => {
  const hostRef = useRef(null);
  const [loadState, setLoadState] = useState({ key: '', markup: '', error: '' });
  const requestKey = `${mode}:${src || ''}`;
  const isCurrentRequest = loadState.key === requestKey;
  const markup = isCurrentRequest ? loadState.markup : '';
  const error = !src
    ? 'SVG pendiente de configurar.'
    : (isCurrentRequest ? loadState.error : '');

  const availableSet = useMemo(
    () => new Set(availableValues.map((value) => String(Number(value)))),
    [availableValues]
  );

  useEffect(() => {
    if (!src) return undefined;

    const controller = new AbortController();

    fetch(src, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`No se encontró ${src}`);
        return response.text();
      })
      .then((rawMarkup) => setLoadState({
        key: `${mode}:${src}`,
        markup: normalizeSvgMarkup(rawMarkup, mode),
        error: '',
      }))
      .catch((fetchError) => {
        if (fetchError.name !== 'AbortError') {
          setLoadState({ key: `${mode}:${src}`, markup: '', error: fetchError.message });
        }
      });

    return () => controller.abort();
  }, [mode, src]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !markup) return;

    const selector = mode === 'building' ? '[data-floor]' : '[data-unit]';
    host.querySelectorAll(selector).forEach((node) => {
      const value = node.getAttribute(mode === 'building' ? 'data-floor' : 'data-unit');
      const isAvailable = availableSet.has(String(Number(value)));
      const isSelected = String(Number(selectedValue)) === String(Number(value));

      node.setAttribute('data-available', isAvailable ? 'true' : 'false');
      node.setAttribute('data-selected', isSelected ? 'true' : 'false');
      node.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');

      if (mode === 'floor') {
        node.style.setProperty('--unit-source-color', colorMap[value] || 'var(--color-cta-2, #2d325e)');
      }
    });
  }, [availableSet, colorMap, markup, mode, selectedValue]);

  const selectFromEvent = (event) => {
    const selector = mode === 'building' ? '[data-floor]' : '[data-unit]';
    const target = event.target.closest?.(selector);
    if (!target || target.getAttribute('data-available') !== 'true') return;

    const value = target.getAttribute(mode === 'building' ? 'data-floor' : 'data-unit');
    onSelect?.(value);
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    selectFromEvent(event);
  };

  return (
    <div
      ref={hostRef}
      className={className}
      onClick={selectFromEvent}
      onKeyDown={handleKeyDown}
    >
      {markup ? <div dangerouslySetInnerHTML={{ __html: markup }} /> : null}
      {error ? <p data-svg-placeholder>{error}</p> : null}
    </div>
  );
};

export default InlineAvailabilitySvg;
